import { DataTable, type DataTableColumn, Toggle, useToast } from "@design";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  type DataResponseListNotificationPreferenceResponse,
  errorMessage,
  getPreferencesOptions,
  getPreferencesQueryKey,
  updatePreferencesMutation,
} from "@/api";
import { BannerAction } from "@/components/BannerAction";
import { InlineNotice } from "@/components/InlineNotice";
import { Page } from "@/components/Page";
import { SectionError } from "@/components/section/SectionStates";
import { Text } from "@/components/Text";
import { useViewport } from "@/responsive/viewport";
import { useDesktopPermission } from "./desktopPermission";
import { DEFAULT_CHANNELS, type NotificationEvent, type NotificationRow, toRows } from "./notificationEvents";

type Channel = "inApp" | "desktop";
type ChannelField = keyof typeof DEFAULT_CHANNELS;

interface Change {
  type: NotificationEvent;
  channel: Channel;
  enabled: boolean;
}

function mergeRow(
  data: DataResponseListNotificationPreferenceResponse | undefined,
  type: string,
  patch: Partial<Record<ChannelField, boolean>>,
): DataResponseListNotificationPreferenceResponse | undefined {
  if (!data) return data;
  const exists = data.data.some((p) => p.notificationType === type);
  const rows = exists
    ? data.data.map((p) => (p.notificationType === type ? { ...p, ...patch } : p))
    : [...data.data, { notificationType: type, ...DEFAULT_CHANNELS, ...patch }];
  return { ...data, data: rows };
}

function useToggleNotification() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation("settings");
  const key = getPreferencesQueryKey();
  const update = useMutation({ ...updatePreferencesMutation(), scope: { id: "notification-preferences" } });

  return async ({ type, channel, enabled }: Change) => {
    const field: ChannelField = channel === "inApp" ? "inAppEnabled" : "desktopEnabled";
    await queryClient.cancelQueries({ queryKey: key });
    const previous = queryClient
      .getQueryData<DataResponseListNotificationPreferenceResponse>(key)
      ?.data.find((p) => p.notificationType === type);
    queryClient.setQueryData<DataResponseListNotificationPreferenceResponse>(key, (data) =>
      mergeRow(data, type, { [field]: enabled }),
    );
    try {
      const res = await update.mutateAsync({ body: { preferences: [{ notificationType: type, [field]: enabled }] } });
      const saved = res.data.find((p) => p.notificationType === type);
      if (saved) {
        queryClient.setQueryData<DataResponseListNotificationPreferenceResponse>(key, (data) =>
          mergeRow(data, type, { [field]: saved[field] }),
        );
      }
    } catch (error) {
      queryClient.setQueryData<DataResponseListNotificationPreferenceResponse>(key, (data) =>
        mergeRow(data, type, { [field]: previous?.[field] ?? DEFAULT_CHANNELS[field] }),
      );
      toast.error(errorMessage(error, t("notifications.saveFailed")));
    }
  };
}

function useColumns(onToggle: (change: Change) => void, showDesktop: boolean): DataTableColumn<NotificationRow>[] {
  const { t } = useTranslation("settings");
  const columns: DataTableColumn<NotificationRow>[] = [
    {
      key: "event",
      header: t("notifications.columns.event"),
      mobile: "primary",
      render: (row) => (
        <div className="flex min-w-0 flex-col gap-0.5 text-left">
          <Text as="span" variant="body-sm">
            {t(`notifications.events.${row.type}.label`)}
          </Text>
          <Text as="span" variant="caption" tone="tertiary">
            {t(`notifications.events.${row.type}.description`)}
          </Text>
        </div>
      ),
    },
    {
      key: "inApp",
      header: t("notifications.columns.inApp"),
      interactive: true,
      render: (row) => (
        <Toggle
          aria-label={t("notifications.toggleLabel", {
            event: t(`notifications.events.${row.type}.label`),
            channel: t("notifications.columns.inApp"),
          })}
          checked={row.inApp}
          onChange={(enabled) => onToggle({ type: row.type, channel: "inApp", enabled })}
        />
      ),
    },
  ];
  if (showDesktop) {
    columns.push({
      key: "desktop",
      header: t("notifications.columns.desktop"),
      interactive: true,
      render: (row) => (
        <Toggle
          aria-label={t("notifications.toggleLabel", {
            event: t(`notifications.events.${row.type}.label`),
            channel: t("notifications.columns.desktop"),
          })}
          checked={row.desktop}
          onChange={(enabled) => onToggle({ type: row.type, channel: "desktop", enabled })}
        />
      ),
    });
  }
  return columns;
}

/** Desktop notifications already on for the account, but this browser hasn't been asked (e.g. a new browser). */
function PermissionPrompt({ desktopOn, onEnable }: Readonly<{ desktopOn: boolean; onEnable: () => void }>) {
  const { t } = useTranslation("settings");
  if (!desktopOn) return <InlineNotice>{t("notifications.permissionPrompt")}</InlineNotice>;
  return (
    <InlineNotice action={<BannerAction onClick={onEnable}>{t("notifications.enableInBrowser")}</BannerAction>}>
      {t("notifications.permissionNeeded")}
    </InlineNotice>
  );
}

export function NotificationsPage() {
  const { t } = useTranslation("settings");
  const { isMobile } = useViewport();
  const preferences = useQuery(getPreferencesOptions());
  const { permission, request } = useDesktopPermission();
  const toggle = useToggleNotification();
  const showDesktop = !isMobile && permission !== "unsupported";

  const onToggle = async (change: Change) => {
    if (change.channel === "desktop" && change.enabled && !(await request())) return;
    await toggle(change);
  };
  const columns = useColumns((change) => void onToggle(change), showDesktop);
  const desktopOn = toRows(preferences.data?.data).some((row) => row.desktop);

  return (
    <Page title={t("notifications.title")} subtitle={t("notifications.subtitle")}>
      {showDesktop && permission === "denied" && (
        <InlineNotice tone="warning">{t("notifications.permissionDenied")}</InlineNotice>
      )}
      {showDesktop && permission === "default" && preferences.isSuccess && (
        <PermissionPrompt desktopOn={desktopOn} onEnable={() => void request()} />
      )}
      {preferences.isError ? (
        <SectionError onRetry={() => void preferences.refetch()} error={preferences.error} />
      ) : (
        <DataTable
          aria-label={t("notifications.tableLabel")}
          columns={columns}
          rows={toRows(preferences.data?.data)}
          rowKey={(row) => row.type}
          loading={preferences.isPending}
        />
      )}
    </Page>
  );
}
